"use client";

import { cn } from "@workspace/ui/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui/components/select";
import {
  getStatusColor,
  getNextStatuses,
  getValidStatuses,
  STATUS_COLOR_CLASSES,
} from "@/lib/frontmatter/status-transitions";
import type { DocType } from "@/lib/frontmatter/types";

interface StatusBadgeProps {
  status: string;
  docType?: DocType;
  editing?: boolean;
  onChange?: (status: string) => void;
}

export function StatusBadge({
  status,
  docType,
  editing,
  onChange,
}: StatusBadgeProps) {
  const color = getStatusColor(status);
  const colorClasses = STATUS_COLOR_CLASSES[color];

  if (!editing || !docType) {
    return (
      <span
        className={cn(
          "inline-flex items-center px-2 py-0.5 rounded text-sm font-medium",
          colorClasses
        )}
      >
        {status}
      </span>
    );
  }

  // In edit mode, show valid transitions + current status
  const nextStatuses = getNextStatuses(docType, status);
  const allStatuses = getValidStatuses(docType);
  // Show current + valid next transitions; fall back to all if current isn't in the map
  const options =
    nextStatuses.length > 0
      ? [status, ...nextStatuses.filter((s) => s !== status)]
      : allStatuses;

  return (
    <Select value={status} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-sm w-auto min-w-[100px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((s) => {
          const sColor = getStatusColor(s);
          return (
            <SelectItem key={s} value={s} className="text-sm">
              <span className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "w-2 h-2 rounded-full",
                    sColor === "green" && "bg-emerald-500",
                    sColor === "yellow" && "bg-amber-500",
                    sColor === "blue" && "bg-blue-500",
                    sColor === "red" && "bg-red-500",
                    sColor === "gray" && "bg-zinc-400",
                    sColor === "purple" && "bg-purple-500"
                  )}
                />
                {s}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
